// LeadershipCapstonePage — AI-graded 90-Day Action Plan submission flow.
//
// Boardroom-themed (navy + cyan + gold) form with 5 structured questions.
// Submits to POST /api/quick-bites/ai-for-leaders/capstone/submit which
// returns Claude-graded executive feedback (0-100 score + per-question
// commentary + 3 priority actions + pass/fail recommendation).

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Briefcase, Award, CheckCircle2, AlertCircle, Loader2,
  FileDown, ChevronRight, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NAVY = "#0a1f44";
const CYAN = "#06b6d4";
const GOLD = "#c9a35a";

export default function LeadershipCapstonePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [questions, setQuestions] = useState([]);
  const [learnerName, setLearnerName] = useState("");
  const [answers, setAnswers] = useState({});
  const [savedGrading, setSavedGrading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("loading"); // loading | form | result | unauth
  const [artifacts, setArtifacts] = useState([]);

  // Fetch questions + any saved grading
  useEffect(() => {
    if (!token) { setStep("unauth"); return; }
    let cancelled = false;
    const loadCapstone = async () => {
      try {
        const r = await axios.get(`${API}/quick-bites/ai-for-leaders/capstone?token=${token}`);
        if (cancelled) return;
        setQuestions(r.data.questions || []);
        setLearnerName(r.data.learner_name || "");
        if (r.data.saved && r.data.saved.grading) {
          setSavedGrading(r.data.saved.grading);
          setAnswers(r.data.saved.answers || {});
          setStep("result");
        } else {
          setStep("form");
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) setStep("unauth");
        else setError(e?.response?.data?.detail || "Failed to load capstone");
      }
      try {
        const r = await axios.get(`${API}/quick-bites/ai-for-leaders/artifacts?token=${token}`);
        if (!cancelled) setArtifacts(r.data.items || []);
      } catch {
        /* artifacts list is optional */
      }
    };
    loadCapstone();
    return () => { cancelled = true; };
  }, [token]);

  function wordCount(s) { return (s || "").trim().split(/\s+/).filter(Boolean).length; }

  async function submit() {
    setError("");
    for (const q of questions) {
      const wc = wordCount(answers[q.id]);
      if (wc < q.min_words) {
        setError(`"${q.title}" needs at least ${q.min_words} words (you've written ${wc}).`);
        document.getElementById(q.id)?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/quick-bites/ai-for-leaders/capstone/submit`, {
        token, answers,
      });
      setSavedGrading(r.data.grading);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (step === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="animate-spin text-cyan-400" size={32} />
      </div>
    );
  }
  if (step === "unauth") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: NAVY }}>
        <AlertCircle className="text-red-400 mb-3" size={36} />
        <p className="text-white text-xl font-bold mb-2">Access required</p>
        <p className="text-zinc-400 text-sm">This capstone is for paid AI for Leaders enrollees only.</p>
        <Link to="/courses/qb-ai-for-leaders" className="mt-5 text-cyan-300 text-sm">Back to course →</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: NAVY }} data-testid="leadership-capstone-page">
      {/* Boardroom header */}
      <header className="border-b" style={{ borderColor: "rgba(6,182,212,0.2)" }}>
        <div className="max-w-5xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/learn/qb-ai-for-leaders" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back to course
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-extrabold text-cyan-300">
            <Briefcase size={11} /> Boardroom Capstone
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-10">
        {step === "form" && (
          <FormView
            questions={questions}
            answers={answers}
            setAnswers={setAnswers}
            learnerName={learnerName}
            wordCount={wordCount}
            error={error}
            submitting={submitting}
            onSubmit={submit}
          />
        )}
        {step === "result" && (
          <ResultView
            grading={savedGrading}
            learnerName={learnerName}
            artifacts={artifacts}
            onResubmit={() => { setStep("form"); setSavedGrading(null); window.scrollTo({top:0}); }}
          />
        )}
      </div>
    </div>
  );
}

function FormView({ questions, answers, setAnswers, learnerName, wordCount, error, submitting, onSubmit }) {
  return (
    <>
      <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold mb-2" style={{ color: CYAN }}>
        Final Module · Capstone Submission
      </p>
      <h1 className="text-white text-3xl sm:text-4xl font-extrabold leading-tight mb-2">
        Your 90-Day AI Action Plan
      </h1>
      <p className="text-zinc-400 text-base mb-1">
        Answer all 5 questions thoughtfully — your plan will be reviewed by Claude (Anthropic&apos;s executive AI advisor model)
        and you&apos;ll receive board-grade written feedback within ~30 seconds.
      </p>
      {learnerName && (
        <p className="text-zinc-500 text-sm mb-8 italic">Preparing executive review for: <span className="text-white">{learnerName}</span></p>
      )}

      <div className="space-y-6 mb-8">
        {questions.map((q, i) => {
          const wc = wordCount(answers[q.id]);
          const ok = wc >= q.min_words;
          return (
            <div
              key={q.id}
              id={q.id}
              className="rounded-2xl border p-5 sm:p-6"
              style={{ borderColor: "rgba(6,182,212,0.25)", background: "rgba(255,255,255,0.02)" }}
              data-testid={`capstone-q-${q.id}`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-cyan-300 font-extrabold text-sm">Q{i + 1}</span>
                <h3 className="text-white text-lg font-bold">{q.title}</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-3">{q.prompt}</p>
              <textarea
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                rows={6}
                className="w-full bg-zinc-900/70 border border-zinc-700 rounded-lg p-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400/60 font-mono"
                placeholder={`At least ${q.min_words} words…`}
                data-testid={`capstone-answer-${q.id}`}
              />
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className={ok ? "text-emerald-300" : "text-zinc-500"}>
                  {ok ? "✓" : "•"} {wc} / {q.min_words} min words
                </span>
                <span className="text-zinc-600">Be specific. Use real names, dates, metrics.</span>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-900/20 p-3 mb-4 text-red-300 text-sm" data-testid="capstone-error">
          {error}
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={submitting}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-extrabold text-sm disabled:opacity-60"
        style={{ background: `linear-gradient(135deg, ${CYAN}, #0891b2)`, boxShadow: "0 12px 30px -10px rgba(6,182,212,0.55)" }}
        data-testid="capstone-submit-btn"
      >
        {submitting ? (<><Loader2 className="animate-spin" size={16} /> Claude is reviewing your plan…</>) :
          (<><Sparkles size={16} /> Submit for AI-Graded Feedback <ChevronRight size={16} /></>)}
      </button>
    </>
  );
}

function ResultView({ grading, learnerName, artifacts, onResubmit }) {
  const pass = grading?.pass_recommendation;
  const score = grading?.overall_score || 0;
  const summary = grading?.executive_summary || "";
  const actions = grading?.priority_actions || [];
  const perQ = grading?.per_question_feedback || [];
  const qTitles = useMemo(() => ({
    q1_current_state: "Current State",
    q2_top_3_use_cases: "Top 3 Use Cases",
    q3_vendor_strategy: "Build vs Buy",
    q4_governance_plan: "Governance Plan",
    q5_success_metrics: "Stakeholders & Metrics",
  }), []);

  return (
    <div data-testid="capstone-result">
      <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold mb-2" style={{ color: pass ? "#34d399" : GOLD }}>
        Executive Review · {pass ? "Plan Approved" : "Plan Needs Sharpening"}
      </p>
      <h1 className="text-white text-3xl sm:text-4xl font-extrabold leading-tight mb-3">
        {pass ? "Your 90-day plan is board-ready." : "You're close — one round of edits."}
      </h1>
      <p className="text-zinc-400 text-base mb-8">
        Reviewed by Claude (Anthropic Sonnet 4.5){learnerName ? ` for ${learnerName}` : ""}.
      </p>

      {/* Score gauge */}
      <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(201,163,90,0.35)", background: "rgba(255,255,255,0.03)" }}>
        <p className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-1">Overall Score</p>
        <div className="flex items-baseline gap-2">
          <span className="text-6xl font-extrabold text-white" data-testid="capstone-score">{score}</span>
          <span className="text-zinc-500 text-2xl font-bold">/100</span>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${pass ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/20 text-amber-200 border border-amber-500/40"}`}>
            {pass ? "✓ Pass · ≥70" : "Needs ≥70 to pass"}
          </span>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, score)}%`, background: `linear-gradient(90deg, ${CYAN}, ${pass ? "#10b981" : GOLD})` }} />
        </div>
      </div>

      {/* Executive summary */}
      <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(6,182,212,0.30)" }}>
        <p className="text-cyan-300 text-xs uppercase tracking-wider font-extrabold mb-2 inline-flex items-center gap-1.5"><Briefcase size={11} /> Executive Summary</p>
        <p className="text-zinc-200 text-base leading-relaxed">{summary}</p>
      </div>

      {/* Priority actions */}
      {actions.length > 0 && (
        <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(201,163,90,0.35)" }}>
          <p className="text-amber-200 text-xs uppercase tracking-wider font-extrabold mb-3">Top 3 Priority Actions · Next 30 Days</p>
          <ol className="space-y-2.5">
            {actions.map((a, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-extrabold text-xs text-black" style={{ background: GOLD }}>{i + 1}</span>
                <span className="text-zinc-200 text-sm leading-snug">{a}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Per-question detail */}
      <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <p className="text-zinc-400 text-xs uppercase tracking-wider font-extrabold mb-4">Per-Question Feedback</p>
        <div className="space-y-4">
          {perQ.map((p) => (
            <div key={p.id} className="border-l-2 pl-4" style={{ borderColor: CYAN }}>
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-white text-sm font-bold">{qTitles[p.id] || p.id}</p>
                <span className="text-cyan-300 text-xs font-extrabold whitespace-nowrap">{p.score}/20</span>
              </div>
              <p className="text-zinc-400 text-sm mt-1.5 leading-relaxed">{p.feedback}</p>
            </div>
          ))}
        </div>
      </div>

      {/* PDF artifacts */}
      {artifacts.length > 0 && (
        <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(201,163,90,0.35)", background: "linear-gradient(135deg, rgba(201,163,90,0.06), transparent)" }}>
          <p className="text-amber-200 text-xs uppercase tracking-wider font-extrabold mb-3 inline-flex items-center gap-1.5">
            <FileDown size={12} /> Boardroom Templates · Yours to Keep
          </p>
          <ul className="space-y-2">
            {artifacts.map((a) => (
              <li key={a.slug}>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${a.download_url}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between gap-3 rounded-lg border border-zinc-700/60 hover:border-cyan-400/40 p-3 transition-colors"
                  data-testid={`download-${a.slug}`}
                >
                  <div>
                    <p className="text-white text-sm font-bold">{a.title}</p>
                    <p className="text-zinc-500 text-xs">Module {a.module} companion · PDF, A4</p>
                  </div>
                  <FileDown className="text-amber-300" size={18} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={onResubmit}
          className="px-5 py-2.5 rounded-lg border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-sm font-bold"
          data-testid="capstone-edit-btn"
        >
          Edit & Resubmit
        </button>
        <Link
          to="/learn/qb-ai-for-leaders"
          className="px-5 py-2.5 rounded-lg text-white font-bold text-sm inline-flex items-center gap-1.5"
          style={{ background: `linear-gradient(135deg, ${CYAN}, #0891b2)` }}
        >
          <Award size={14} /> Continue to Final Exam <ChevronRight size={14} />
        </Link>
      </div>
    </div>
  );
}
